import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';
import ImageUpload from '../../components/ImageUpload';
import { clothingSizes, isSizelessCategoryName, productHasClothingSizes } from '../../utils/productSize';

const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL', '6XL', '7XL'];
const initialProduct = {
  name: '',
  description: '',
  wholesalePrice: '',
  retailPrice: '',
  price: '',
  category: '',
  subcategory: '',
  images: [],
  stock: 0,
  sizes: [],
  colors: [],
  isActive: true,
  rating: 5
};

const ProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [product, setProduct] = useState(initialProduct);
  const [imageUrls, setImageUrls] = useState([]);
  const [search, setSearch] = useState('');
  const [colorInputString, setColorInputString] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [sizeMode, setSizeMode] = useState('size');

  // Function to fetch products
  const fetchProducts = async () => {
    try {
      const res = await fetch(getApiUrl('/api/admin/products'), { headers: { 'x-admin': 'true' } });
      const data = await res.json();
      console.log('Products API Response:', data); // Debug log
      
      // Handle different response formats
      let productsList = [];
      if (data && Array.isArray(data.products)) {
        productsList = data.products;
      } else if (data && Array.isArray(data)) {
        productsList = data;
      }
      
      console.log('Processed Products List:', productsList); // Debug log
      setProducts(productsList);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  // Fetch products, categories, and subcategories
  useEffect(() => {
    console.log('Component mounted, fetching products...'); // Debug log
    fetchProducts();

    // Fetch categories
    fetch(getApiUrl('/api/admin/categories'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.categories)) setCategories(data.categories);
        else if (Array.isArray(data)) setCategories(data);
        else setCategories([]);
      })
      .catch(error => console.error('Error fetching categories:', error));

    // Fetch subcategories
    fetch(getApiUrl('/api/admin/subcategories'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => setSubcategories(Array.isArray(data) ? data : []))
      .catch(error => console.error('Error fetching subcategories:', error));
  }, []);

  const selectedCategoryName =
    categories.find((cat) => cat._id === product.category)?.name || '';
  const selectedSubcategoryName =
    subcategories.find((sub) => sub._id === product.subcategory)?.name || '';
  const allowsOptionalSize =
    isSizelessCategoryName(selectedCategoryName) || isSizelessCategoryName(selectedSubcategoryName);
  const withoutSize = allowsOptionalSize && sizeMode === 'none';

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextCategory = name === 'category' ? value : product.category;
    const nextSubcategory = name === 'subcategory' ? value : name === 'category' ? '' : product.subcategory;
    const categoryName = categories.find((cat) => cat._id === nextCategory)?.name || '';
    const subcategoryName = subcategories.find((sub) => sub._id === nextSubcategory)?.name || '';
    const optional =
      isSizelessCategoryName(categoryName) || isSizelessCategoryName(subcategoryName);

    const nextValue =
      type === 'checkbox'
        ? checked
        : name === 'stock' && value !== '' && Number(value) < 0
          ? '0'
          : value;
    setProduct(prev => ({
      ...prev,
      [name]: nextValue,
      // Clear subcategory when category changes
      ...(name === 'category' && { subcategory: '' })
    }));

    if (name === 'category' || name === 'subcategory') {
      setSizeMode(optional && !clothingSizes(product.sizes).length ? 'none' : 'size');
    }
  };

  // Handle sizes (checkbox group)
  const handleSizeChange = (e) => {
    const { value, checked } = e.target;
    setProduct(prev => ({
      ...prev,
      sizes: checked
        ? [...(prev.sizes || []), value]
        : (prev.sizes || []).filter(size => size !== value),
    }));
  };

  // Handle colors (comma separated) - This will now directly update colorInputString
  const handleColorInputChange = (e) => {
    setColorInputString(e.target.value);
  };

  // Handle image upload
  const handleImagesChange = (urls) => {
    console.log('handleImagesChange called with:', urls);
    setImageUrls(urls);
  };

  const allowsOptionalSizeFor = (prod) => {
    const categoryId = prod.category?._id || prod.category;
    const subcategoryId = prod.subcategory?._id || prod.subcategory;
    const categoryName =
      (typeof prod.category === 'object' && prod.category?.name) ||
      categories.find((cat) => cat._id === categoryId)?.name ||
      '';
    const subcategoryName =
      (typeof prod.subcategory === 'object' && prod.subcategory?.name) ||
      subcategories.find((sub) => sub._id === subcategoryId)?.name ||
      '';
    return isSizelessCategoryName(categoryName) || isSizelessCategoryName(subcategoryName);
  };

  // Handle edit
  const handleEdit = (prod) => {
    // Find the correct category and subcategory _id
    let categoryId = prod.category?._id || prod.category;
    if (typeof categoryId !== 'string') {
      const found = categories.find(cat => cat.name === prod.category);
      if (found) categoryId = found._id;
    }
    let subcategoryId = prod.subcategory?._id || prod.subcategory;
    if (typeof subcategoryId !== 'string') {
      const found = subcategories.find(sub => 
        sub.name === prod.subcategory && 
        (sub.category?._id || sub.category) === categoryId
      );
      if (found) subcategoryId = found._id;
      else console.log('Subcategory not found for', prod.subcategory, 'with category', categoryId);
    }
    setEditingProduct(prod);
    setProduct({
      ...prod,
      price: prod.price !== undefined && prod.price !== null ? prod.price.toString() : '',
      wholesalePrice: prod.wholesalePrice !== undefined && prod.wholesalePrice !== null ? prod.wholesalePrice.toString() : '',
      retailPrice: String(prod.retailPrice || prod.oldPrice || prod.price || ''),
      stock: prod.stock !== undefined && prod.stock !== null ? String(Math.max(0, Number(prod.stock) || 0)) : '0',
      category: categoryId || '',
      subcategory: subcategoryId || '',
      // No longer need to format prod.colors for display here
      // The actual product.colors will be set when the form is submitted
    });
    console.log('Setting imageUrls for edit:', prod.images);
    setImageUrls(prod.images || []);
    // Initialize colorInputString from existing product colors for editing
    setColorInputString(Array.isArray(prod.colors) ? prod.colors.join(', ') : prod.colors || '');
    setTagsInput(prod.tags ? prod.tags.join(', ') : '');
    setSizeMode(allowsOptionalSizeFor(prod) && !productHasClothingSizes(prod) ? 'none' : 'size');
    setShowModal(true);
  };

  // Handle form submission - Parse colorInputString here
  const handleSubmit = async (e) => {
    e.preventDefault();
    const tags = tagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    console.log('Submitting productData with images:', imageUrls);
    const retail = parseFloat(product.retailPrice) || 0;
    const discounted = product.price === '' || product.price == null
      ? retail
      : parseFloat(product.price) || retail;
    const productData = { 
      ...product, 
      images: imageUrls,
      price: discounted,
      wholesalePrice: parseFloat(product.wholesalePrice) || 0,
      retailPrice: retail || discounted,
      oldPrice: retail > discounted ? retail : null,
      stock: Math.max(0, parseInt(product.stock, 10) || 0),
      category: product.category,
      subcategory: product.subcategory,
      sizes: withoutSize ? [] : clothingSizes(product.sizes),
      colors: colorInputString.split(',').map(c => c.trim()).filter(c => c), // Parse from colorInputString
      tags: tags,
    };
    try {
      const url = editingProduct 
        ? getApiUrl(`/api/admin/products/${editingProduct._id}`)
        : getApiUrl('/api/admin/products');
      
      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'x-admin': 'true',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingProduct ? 'update' : 'create'} product`);
      }
      
      const payload = await response.json();
      const saved = payload.product || payload;
      if (editingProduct) {
        setProducts(prev => prev.map(p => p._id === editingProduct._id ? saved : p));
      } else {
        setProducts(prev => [...prev, saved]);
      }
      fetchProducts();
      
      setShowModal(false);
      setProduct(initialProduct);
      setImageUrls([]);
      setEditingProduct(null);
      setTagsInput('');
      setSizeMode('size');
    } catch (error) {
      console.error('Error saving product:', error);
      alert(`Failed to ${editingProduct ? 'update' : 'create'} product. Please try again.`);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      const res = await fetch(getApiUrl(`/api/admin/products/${id}`), {
        method: 'DELETE',
        headers: { 'x-admin': 'true' },
      });
      if (!res.ok) {
        throw new Error('Failed to delete product');
      }
      // Instead of re-fetching, remove the deleted product from state
      setProducts(prev => prev.filter(p => p._id !== id));
    } catch (error) {
      console.error('Error deleting product:', error);
      alert(`Failed to delete product: ${error.message}`);
    }
  };

  // Filter products by search and exclude rental category
  const filteredProducts = Array.isArray(products)
    ? products.filter(prod => {
        // Find the category name for this product
        const categoryObj = categories.find(cat => cat._id === (prod.category?._id || prod.category));
        const isRental = categoryObj && categoryObj.name && categoryObj.name.toLowerCase() === 'rental';
        return !isRental && (
          ((prod.name || '').toLowerCase().includes(search.toLowerCase()) ||
           (categoryObj?.name || '').toLowerCase().includes(search.toLowerCase()))
        );
      })
    : [];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <h1 className="text-xl sm:text-2xl font-bold">Products</h1>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Link
            to="/admin/barcodes"
            className="bg-pink-600 text-white px-4 py-2 rounded-lg flex items-center justify-center"
          >
            Barcodes
          </Link>
          <button
            onClick={() => {
              setEditingProduct(null);
              setProduct(initialProduct);
              setImageUrls([]);
              setColorInputString('');
              setTagsInput('');
              setShowModal(true);
              setSizeMode('size');
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center justify-center w-full sm:w-auto"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Product
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
        </div>
      </div>

      {/* Desktop Products table */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wholesale</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retail</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discounted</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredProducts.map((product) => (
              <tr key={product._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="h-12 w-12 object-cover rounded"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-gray-200 rounded"></div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{product.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">₹{product.wholesalePrice || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={product.retailPrice > product.price ? 'text-gray-400 line-through' : ''}>
                    ₹{product.retailPrice || product.oldPrice || product.price}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-pink-600 font-semibold">₹{product.price}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {typeof product.category === 'object' ? product.category?.name : product.category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button className="text-blue-500 hover:text-blue-700 mr-2" onClick={() => handleEdit(product)}>
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button className="text-red-500 hover:text-red-700" onClick={() => handleDelete(product._id)}>
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Products cards */}
      <div className="md:hidden space-y-4">
        {filteredProducts.map((product) => (
          <div key={product._id} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="h-16 w-16 object-cover rounded"
                  />
                ) : (
                  <div className="h-16 w-16 bg-gray-200 rounded"></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 truncate">{product.name}</h3>
                <p className="text-sm text-gray-500">
                  W ₹{product.wholesalePrice || 0}
                  {product.retailPrice > product.price ? (
                    <>
                      {' '}· <span className="line-through">R ₹{product.retailPrice}</span>
                      {' '}· D ₹{product.price}
                    </>
                  ) : (
                    <> · R ₹{product.retailPrice || product.price}</>
                  )}
                </p>
                <p className="text-xs text-gray-400">{product.category}</p>
              </div>
              <div className="flex space-x-2">
                <button 
                  className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded" 
                  onClick={() => handleEdit(product)}
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button 
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded" 
                  onClick={() => handleDelete(product._id)}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={product.name}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    value={product.description}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Wholesale price</label>
                    <input
                      type="number"
                      name="wholesalePrice"
                      value={product.wholesalePrice}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2"
                      min="0"
                      step="1"
                      required
                      placeholder="e.g. 800"
                    />
                    <p className="mt-1 text-xs text-gray-500">Barcodes and catalogue.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Retail price</label>
                    <input
                      type="number"
                      name="retailPrice"
                      value={product.retailPrice}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2"
                      min="0"
                      step="1"
                      required
                      placeholder="e.g. 1800"
                    />
                    <p className="mt-1 text-xs text-gray-500">MRP — shown crossed out on cards.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Discounted price <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={product.price}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2"
                      min="0"
                      step="1"
                      placeholder="e.g. 1400"
                    />
                    <p className="mt-1 text-xs text-gray-500">Live selling price on cards. Leave empty to use retail.</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Stock</label>
                  <input
                    type="number"
                    name="stock"
                    value={product.stock}
                    onChange={handleChange}
                    required
                    min="0"
                    step="1"
                    onKeyDown={(event) => {
                      if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
                        event.preventDefault();
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    name="category"
                    value={product.category}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subcategory</label>
                  <select
                    name="subcategory"
                    value={product.subcategory}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select a subcategory</option>
                    {subcategories
                      .filter(sub => {
                        const subCatId = sub.category?._id || sub.category;
                        return subCatId === product.category;
                      })
                      .map(subcategory => (
                        <option key={subcategory._id} value={subcategory._id}>
                          {subcategory.name}
                        </option>
                    ))}
                  </select>
                  {product.category && subcategories.filter(sub => (sub.category?._id || sub.category) === product.category).length === 0 && (
                    <div className="text-sm text-red-500 mt-1">No subcategories available for this category.</div>
                  )}
                </div>
                <div>
                  {allowsOptionalSize ? (
                    <label className="block text-sm font-medium text-gray-700">
                      Size option
                      <select
                        value={sizeMode}
                        onChange={(e) => setSizeMode(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="size">Size</option>
                        <option value="none">Without size</option>
                      </select>
                    </label>
                  ) : (
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sizes</label>
                  )}
                  {withoutSize ? (
                    <p className="text-sm text-gray-500 mt-2">
                      No clothing size will be required for this product.
                    </p>
                  ) : (
                    <div className={`flex flex-wrap gap-2 ${allowsOptionalSize ? 'mt-3' : ''}`}>
                      {sizeOptions.map(size => (
                        <label key={size} className="inline-flex items-center">
                          <input
                            type="checkbox"
                            value={size}
                            checked={product.sizes?.includes(size)}
                            onChange={handleSizeChange}
                            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{size}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Colors (comma separated)</label>
                  <input
                    type="text"
                    value={colorInputString}
                    onChange={handleColorInputChange}
                    placeholder="e.g., Red, Blue, Green"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated, optional)</label>
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={e => setTagsInput(e.target.value)}
                    placeholder="e.g. gallery, women-collection"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 mb-4"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Images</label>
                  <ImageUpload
                    onImagesChange={handleImagesChange}
                    multiple={true}
                    maxFiles={5}
                    accept="image/*"
                    className="mt-1"
                    initialImages={imageUrls}
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={product.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                    setProduct(initialProduct);
                    setImageUrls([]);
                    setTagsInput('');
                    setSizeMode('size');
                  }}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingProduct ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsAdmin; 